import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { useKyc } from '../context/KycContext';
import { useTheme } from '../context/ThemeContext';

type Tab = {
  to: string;
  labelKey: string;
  icon: string;
  center?: boolean;
  matches: (path: string) => boolean;
};

const accountRoutes = ['/profile', '/transactions', '/order', '/payout-methods', '/notifications', '/notification-prefs', '/faq', '/help', '/events'];

const tabs: Tab[] = [
  { to: '/home', labelKey: 'nav.home', icon: 'house', matches: path => path === '/home' },
  { to: '/games', labelKey: 'nav.games', icon: 'gamepad', matches: path => path.startsWith('/games') || path.startsWith('/topup') },
  { to: '/wallet-topup', labelKey: 'nav.addFunds', icon: 'plus', center: true, matches: path => path.startsWith('/wallet-topup') },
  { to: '/withdraw', labelKey: 'nav.cashOut', icon: 'arrow-down', matches: path => path.startsWith('/withdraw') },
  { to: '/profile', labelKey: 'nav.account', icon: 'user', matches: path => accountRoutes.some(route => path.startsWith(route)) },
];

export default function BottomTabBar() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { pathname } = useLocation();
  const { mustVerify } = useKyc();

  return (
    <nav
      className="sls-bottom-nav"
      data-tour="bottom-nav"
      aria-label={t('nav.main', 'Main navigation')}
      style={{
        position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 0,
        width: '100%', maxWidth: 480,
        minHeight: 'calc(76px + env(safe-area-inset-bottom, 0px))',
        padding: '4px 6px env(safe-area-inset-bottom, 0px)',
        backgroundColor: colors.tabBar, borderTop: `1px solid ${colors.line}`,
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', zIndex: 100,
      }}
    >
      {tabs.map(tab => {
        const active = tab.matches(pathname);
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            data-tour={`tab-${tab.to.slice(1)}`}
            aria-current={active ? 'page' : undefined}
            style={{
              minWidth: 0, minHeight: 68, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 4,
              color: active ? colors.accent : colors.textMuted,
              textDecoration: 'none', fontSize: 14, fontWeight: active ? 600 : 400,
              textAlign: 'center', lineHeight: 1.05,
            }}
          >
            <span style={{
              position: 'relative', width: tab.center ? 42 : 32, height: tab.center ? 42 : 28,
              borderRadius: tab.center ? 21 : 0,
              backgroundColor: tab.center ? colors.accent : 'transparent',
              display: 'grid', placeItems: 'center',
            }}>
              <Icon name={tab.icon} size={tab.center ? 19 : 20} color={tab.center ? colors.textInverse : active ? colors.accent : colors.textMuted} />
              {tab.to === '/profile' && mustVerify ? (
                <span aria-label={t('kyc.needsVerification', 'Identity verification needed')} style={{ position: 'absolute', top: 0, right: 1, width: 8, height: 8, borderRadius: 4, backgroundColor: colors.text }} />
              ) : null}
            </span>
            <span>{t(tab.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
